import React from 'react';
import { Link } from 'react-router-dom';
import { FormErrorMessage } from 'src/shared/design-system/Input/Error/FormErrorMessage';
import { Routes } from 'src/shared/Routes';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { BaseInput, Checkbox } from 'web-common/src/shared/design-system/components/Input';
import { useTermsConsentSentence } from './termsConsentSentence';
import { SignUpWithEmailProps, useSignUpWithEmailForm } from './useSignUpWithEmailForm';

/**
 * The sign-up screen as frames 360:7933 and 361:10693 draw it. Two differences from control, both
 * visual: the name fields are separate where control joins them into one DualLabeledTextInput, and
 * the labels are title case.
 *
 * Behaviour comes from {@link useSignUpWithEmailForm}, which control shares, so the two arms
 * validate and register identically.
 */
export const SignUpGateSignUpForm: React.FC<SignUpWithEmailProps> = (props) => {
  const { email, onEmailChange, requireZipCode, signupCTA } = props;
  const consentSentence = useTermsConsentSentence();
  const {
    firstName,
    onFirstNameChange,
    lastName,
    onLastNameChange,
    zipCode,
    onZipCodeChange,
    displayPhone,
    onPhoneChange,
    password,
    onPasswordChange,
    tosChecked,
    toggleTos,
    emailErrorMessage,
    nameErrorMessage,
    zipCodeErrorMessage,
    passwordErrorMessage,
    phoneErrorMessage,
    signUpErrorMessage,
    loading,
    handleSubmitForm,
    handleKeyDown,
  } = useSignUpWithEmailForm(props);

  return (
    <form onSubmit={handleSubmitForm} onKeyDown={handleKeyDown} data-cy='sign-up-form'>
      <fieldset className='flex flex-col gap-2 border-none p-0' disabled={loading}>
        <BaseInput
          label='Email'
          name='email'
          value={email}
          onChange={onEmailChange}
          errorMessage={emailErrorMessage}
          hasError={!!emailErrorMessage}
          data-cy='sign-up-email'
        />
        <BaseInput
          label='First Name'
          name='firstname'
          value={firstName}
          onChange={onFirstNameChange}
          autoFocus
          data-cy='sign-up-first-name'
        />
        <BaseInput
          label='Last Name'
          name='lastname'
          value={lastName}
          onChange={onLastNameChange}
          errorMessage={nameErrorMessage}
          hasError={!!nameErrorMessage}
          data-cy='sign-up-last-name'
        />
        {requireZipCode && (
          <BaseInput
            label='Zip Code'
            name='zipCode'
            value={zipCode}
            onChange={onZipCodeChange}
            errorMessage={zipCodeErrorMessage}
            hasError={!!zipCodeErrorMessage}
            data-cy='sign-up-zip-code'
          />
        )}
        <BaseInput
          label='Phone Number'
          name='phone'
          value={displayPhone}
          onChange={onPhoneChange}
          errorMessage={phoneErrorMessage}
          hasError={!!phoneErrorMessage}
          data-cy='sign-up-phone'
        />
        <BaseInput
          type='password'
          label='Password'
          name='password'
          value={password}
          onChange={onPasswordChange}
          errorMessage={passwordErrorMessage}
          hasError={!!passwordErrorMessage}
          data-cy='sign-up-password'
        />
        {/* The frame gives the terms block 22px of air on each side, against the 8px the stack
            gives every other child, so it carries 14 of its own on top of that gap. */}
        <div className='my-3.5'>
          <Checkbox
            name='tos'
            checked={tosChecked}
            onChange={toggleTos}
            data-cy='sign-up-tos'
            label={
              <span className='text-label-400 text-eggplant-300'>
                {consentSentence}
                <Link to={Routes.CONSUMER_TOS} target='_blank' className='font-normal text-eggplant-300 underline'>
                  Terms of Service
                </Link>
                {', '}
                <Link
                  to={Routes.CONSUMER_PRIVACY_POLICY}
                  target='_blank'
                  className='font-normal text-eggplant-300 underline'>
                  Privacy Policy
                </Link>
                , and to receive text messages
              </span>
            }
          />
        </div>
        {signUpErrorMessage ? <FormErrorMessage>{signUpErrorMessage}</FormErrorMessage> : null}
        <Button
          type='submit'
          buttonType={ButtonType.Primary}
          renderFullWidth
          disabled={loading}
          data-cy='sign-up-button'>
          {signupCTA ?? 'Sign up'}
        </Button>
      </fieldset>
    </form>
  );
};
